import {
  Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BlockScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { PortfolioService } from 'src/app/core/data-services';
import { DomainService } from 'src/app/core/data-services/domain.service';
import { PaginatedList } from 'src/app/xform-compat';
import { Portfolio } from '../../models';
import { Domain } from '../../models/domain.model';
import { FilterFormData, createDefaultFilterFormData } from '../../models/filter-form-data.interface';
import { FilterChipItem } from '../../models/filter-chip-item';
import { FilterPanePersistenceService } from '../../services/filter-pane-persistence.service';
import { parseMarketCapValue } from '../../utils/market-cap-parser';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/** One table's row count for the drawer's per-table breakdown and its VIEW RESULTS total. */
export interface FilterResultCount {
  label: string;
  filtered: number;
  total: number;
}

/** Phone widths, where the pane becomes a slide-out drawer. Matches the pages' md breakpoint. */
const DRAWER_MEDIA_QUERY = '(max-width: 767.98px)';

@Component({
    selector: 'rms-filter-pane',
    templateUrl: './filter-pane.component.html',
    styleUrls: ['./filter-pane.component.scss'],
    standalone: false
})
export class FilterPaneComponent implements OnInit, OnChanges, OnDestroy {
  /** Whether the filter drawer is open (CSS class binding, not *ngIf). */
  @Input() isOpen = false;
  /** LocalStorage key for this page's independent filter persistence. */
  @Input() storageKey: string;
  /** Whether the user has permission to see portfolio filter groups. */
  @Input() hasPortfolioPermission = true;
  /** Each table's current and unfiltered row counts, for the phone drawer's breakdown and total. */
  @Input() resultCounts: Array<FilterResultCount> = [];
  /**
   * True while any of the page's tables is still reloading. The tables arrive one after another,
   * so until the last one is back `resultCounts` adds up to a number that is about to change; the
   * drawer shows a loader in place of the total rather than a count that climbs.
   */
  @Input() resultsLoading = false;
  /**
   * The page supplies its own filter groups, projected with a `filterGroup` attribute, in place of the
   * Domains / Portfolio / Market Cap groups, and owns the filter state: it passes the Active Filters pills in
   * through `chips`, and the pane reports pill removal and Clear Filters through `chipRemoved` and
   * `filtersCleared` rather than touching its own form. No localStorage persistence in this mode.
   */
  @Input() customGroups = false;
  /** The Active Filters pills when `customGroups` is on. */
  @Input() set chips(value: Array<FilterChipItem>) {
    this.customChips = value || [];
    if (this.customGroups) {
      this.activeFilterChips = this.customChips;
    }
  }
  /** Whether the phone FILTERS button shrinks to a circle on the first page scroll. */
  @Input() collapseFabOnScroll = true;
  @Output() chipRemoved = new EventEmitter<FilterChipItem>();
  @Output() filtersCleared = new EventEmitter<void>();
  @Output() updateFilterEvent = new EventEmitter<FilterFormData>();
  /** Emitted when the user clicks the close/X button inside the pane. */
  @Output() closePaneEvent = new EventEmitter<void>();
  /** Emitted when the phone FILTERS button asks for the pane to open. */
  @Output() openPaneEvent = new EventEmitter<void>();
  @ViewChildren('portfolioInput') portfolioInputs: QueryList<ElementRef>;

  assetsUrls: Record<string, string> = {
    rss: '/assets/icons/rss-icon.svg',
    rssActive: 'assets/icons/rss-icon-active.svg'
  };
  filterBtnActive = false;
  domainIds = new Array<number>();
  portfolios: Array<Portfolio> = [];
  domains: Array<Domain> = [];
  formData: FilterFormData = createDefaultFilterFormData();
  resetFilterSubject = new Subject<any>();
  activeFilterChips: Array<FilterChipItem> = [];
  /** True at phone widths, where the pane renders as a drawer. */
  isMobile = false;
  /** The phone FILTERS button shrinks to a circle on the first page scroll and stays that way. */
  fabCollapsed = false;

  private customChips: Array<FilterChipItem> = [];
  private readonly blockScrollStrategy: BlockScrollStrategy;
  private removeScrollListener: () => void;
  private readonly persistSubject = new Subject<void>();
  private readonly subscriptions = new Subscription();

  /** True when any market cap bucket checkbox is selected. */
  get isMarketCapBucketActive(): boolean {
    return !!(this.formData.mkCapLarge || this.formData.mkCapMedium || this.formData.mkCapSmall || this.formData.mkCapMicro);
  }

  /** True when either market cap range input has a value. */
  get isMarketCapRangeActive(): boolean {
    return !!(this.formData.mkCapLow?.trim() || this.formData.mkCapHigh?.trim());
  }

  /** Rows showing across all tables — the number the drawer's VIEW RESULTS button reports. */
  get totalResults(): number {
    return (this.resultCounts || []).reduce((sum, count) => sum + (count.filtered || 0), 0);
  }

  /** Only the tables the current filters actually reduced; unchanged ones are noise. */
  get changedResultCounts(): Array<FilterResultCount> {
    return (this.resultCounts || []).filter(count => count.filtered !== count.total);
  }

  constructor(
    private readonly portfolioService: PortfolioService,
    private readonly domainService: DomainService,
    private readonly persistenceService: FilterPanePersistenceService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly ngZone: NgZone,
    scrollStrategyOptions: ScrollStrategyOptions,
  ) {
    this.blockScrollStrategy = scrollStrategyOptions.block();
  }

  ngOnInit() {
    this.watchBreakpoint();
    if (this.collapseFabOnScroll) {
      this.watchFirstScroll();
    }
    if (this.customGroups) {
      this.activeFilterChips = this.customChips;
      return;
    }
    this.loadPortfolios();
    this.loadDomains();
    this.hydrateFromStorage();

    // Debounce persistence writes to localStorage
    this.subscriptions.add(
      this.persistSubject.pipe(debounceTime(300)).subscribe(() => {
        if (this.storageKey) {
          this.persistenceService.saveFilters(this.storageKey, this.formData);
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isOpen) {
      this.syncScrollLock();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.removeScrollListener?.();
    this.releaseScrollLock();
  }

  onInputChange(event: any, field: string) {
    if (event.target.classList.contains('portfolio-type')) {
      this.processPortfolioId(event);
      this.emitAndPersist();
      return;
    }

    if (event.target.classList.contains('position-type')) {
      this.processPositionType(event);
      this.emitAndPersist();
      return;
    }

    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    this.formData[field] = value;

    // Market cap mutual exclusivity: checkboxes clear range inputs
    if (['mkCapLarge', 'mkCapMedium', 'mkCapSmall', 'mkCapMicro'].includes(field) && value === true) {
      this.formData.mkCapLow = '';
      this.formData.mkCapHigh = '';
    }

    this.emitAndPersist();
  }

  /** Handles blur/enter on market cap range inputs: validates format and applies mutual exclusivity. */
  onMarketCapRangeChange(field: string) {
    const raw = (this.formData[field] || '').trim();

    if (raw.length === 0) {
      this.formData[field] = '';
      this.emitAndPersist();
      return;
    }

    // Reject input that doesn't parse to a valid market cap value
    if (parseMarketCapValue(raw) === null) {
      this.formData[field] = '';
      this.emitAndPersist();
      return;
    }

    // Valid range value — uncheck all market cap checkboxes
    this.formData.mkCapLarge = false;
    this.formData.mkCapMedium = false;
    this.formData.mkCapSmall = false;
    this.formData.mkCapMicro = false;

    // Auto-swap if Min > Max so the user doesn't end up with an empty result set from a
    // backwards range. Spec HANDOFF-UX §5: silently correct rather than show an error.
    const lowParsed = parseMarketCapValue(this.formData.mkCapLow);
    const highParsed = parseMarketCapValue(this.formData.mkCapHigh);
    if (lowParsed !== null && highParsed !== null && lowParsed > highParsed) {
      const swapped = this.formData.mkCapLow;
      this.formData.mkCapLow = this.formData.mkCapHigh;
      this.formData.mkCapHigh = swapped;
    }

    this.emitAndPersist();
  }

  /** Restricts market cap input to digits, decimal point, and M/B. Blur validates the full value. */
  onMarketCapKeypress(event: KeyboardEvent): void {
    const char = event.key;

    // Allow control keys (backspace, delete, arrows, tab, etc.)
    if (char.length > 1) {
      return;
    }

    // Allow digits, decimal point, M, and B — reject everything else
    if (/[\d.mMbB]/.test(char)) {
      return;
    }

    event.preventDefault();
  }

  /** Auto-uppercases m/b suffix letters as the user types. */
  onMarketCapInput(field: string): void {
    const value = this.formData[field];
    if (value) {
      const uppercased = value.replaceAll(/[mb]/g, (ch: string) => ch.toUpperCase());
      if (uppercased !== value) {
        this.formData[field] = uppercased;
      }
    }
  }

  /** Clears a single market cap range input (Min or Max) without touching the other side. */
  public clearMkCapInput(field: 'mkCapLow' | 'mkCapHigh'): void {
    this.formData[field] = '';
    this.emitAndPersist();
  }

  /** Validates pasted text for market cap inputs, rejecting invalid pastes. */
  onMarketCapPaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') || '';
    if (parseMarketCapValue(pasted) === null) {
      event.preventDefault();
    }
  }

  disableEnterKeyEvent(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  processPositionType(event: any) {
    const positionType = event.target.name.toUpperCase();

    if (event.target.checked) {
      this.formData.positionType.push(positionType);
    } else {
      this.formData.positionType = this.formData.positionType.filter(id => id !== positionType);
    }
  }

  processPortfolioId(event: any) {
    const portfolioId = Number(event.target.dataset.portfolioid);
    if (event.target.checked) {
      this.formData.portfolioIds.push(portfolioId);
    } else {
      this.formData.portfolioIds = this.formData.portfolioIds.filter(id => id !== portfolioId);
    }
  }

  toggleFilterFollowed() {
    this.filterBtnActive = !this.filterBtnActive;
    this.formData.following = !this.formData.following;
    this.emitAndPersist();
  }

  public handleDomainFilterToggle(data: Array<number>) {
    this.formData.domainIds = data;
    this.emitAndPersist();
  }

  public handleFilterReset(): void {
    if (this.customGroups) {
      this.filtersCleared.emit();
      return;
    }

    if (this.formData.following === true) {
      this.filterBtnActive = false;
    }

    this.formData = createDefaultFilterFormData();

    this.portfolioInputs.forEach((input: ElementRef) => {
      input.nativeElement.checked = false;
    });

    if (this.storageKey) {
      this.persistenceService.clearFilters(this.storageKey);
    }

    this.updateFilterEvent.emit(this.formData);
    this.updateActiveFilterChips();
    this.resetFilterSubject.next();
  }

  public closePane(): void {
    this.closePaneEvent.emit();
  }

  public openPane(): void {
    this.openPaneEvent.emit();
  }

  /**
   * Releases the page scroll lock immediately rather than on the next change detection — for a
   * drawer control that closes the drawer and then scrolls the page in the same tick.
   */
  public releaseScrollLock(): void {
    this.blockScrollStrategy.disable();
  }

  public trackByLabel(index: number, count: FilterResultCount): string {
    return count.label;
  }

  /** Removes a single filter when its chip is deleted from the active filters bar. */
  public removeFilter(chip: FilterChipItem): void {
    if (this.customGroups) {
      this.chipRemoved.emit(chip);
      return;
    }

    switch (chip.type) {
      case 'domain':
        this.formData.domainIds = this.formData.domainIds.filter(id => id !== chip.option.id);
        this.resetFilterSubject.next();
        // Re-initialize domain filter state via the subject trick — domain-filter listens to resetFilterSubject
        // but we need to restore the remaining domains, so we set initialDomainIds indirectly
        break;
      case 'marketCapBucket':
        if (chip.option.field) {
          this.formData[chip.option.field] = false;
        }
        break;
      case 'marketCapRange':
        this.formData.mkCapLow = '';
        this.formData.mkCapHigh = '';
        break;
      case 'following':
        this.formData.following = false;
        this.filterBtnActive = false;
        break;
      case 'positionType':
        // Chip label is title-cased for display; internal values are uppercase
        this.formData.positionType = this.formData.positionType.filter(pt => pt !== chip.option.name.toUpperCase());
        break;
      case 'portfolio':
        this.formData.portfolioIds = this.formData.portfolioIds.filter(id => id !== chip.option.id);
        // Uncheck the corresponding portfolio checkbox
        this.portfolioInputs?.forEach((input: ElementRef) => {
          if (Number(input.nativeElement.dataset.portfolioid) === chip.option.id) {
            input.nativeElement.checked = false;
          }
        });
        break;
    }

    this.emitAndPersist();
  }

  /** Builds active filter chip items from the current formData for the pills bar. */
  public updateActiveFilterChips(): void {
    const chips: Array<FilterChipItem> = [];

    // Domain chips
    if (this.formData.domainIds.length > 0) {
      this.formData.domainIds.forEach(domainId => {
        const domain = this.domains.find(d => d.id === domainId);
        const label = domain ? domain.shortName : `Domain ${domainId}`;
        chips.push({
          color: '#2480E6',
          option: { id: domainId, name: label },
          disabled: false,
          type: 'domain',
          displayName: 'name',
        });
      });
    }

    // Portfolio chips
    if (this.formData.portfolioIds.length > 0) {
      this.formData.portfolioIds.forEach(portfolioId => {
        const portfolio = this.portfolios.find(p => p.id === portfolioId);
        const label = portfolio ? portfolio.name : `Portfolio ${portfolioId}`;
        chips.push({
          color: '#6C757D',
          option: { id: portfolioId, name: label },
          disabled: false,
          type: 'portfolio',
          displayName: 'name',
        });
      });
    }

    // Position type chips — title-case only the display label; internal data stays uppercase for backend matching
    this.formData.positionType.forEach(pt => {
      chips.push({
        color: '#6C757D',
        option: { name: pt.charAt(0) + pt.slice(1).toLowerCase() },
        disabled: false,
        type: 'positionType',
        displayName: 'name',
      });
    });

    // Following chip
    if (this.formData.following) {
      chips.push({
        color: '#35B776',
        option: { name: 'Following' },
        disabled: false,
        type: 'following',
        displayName: 'name',
      });
    }

    // Market cap bucket chips — one per selected bucket
    const mkCapBuckets: Array<{ field: string; label: string }> = [
      { field: 'mkCapLarge', label: 'Large (> $10B)' },
      { field: 'mkCapMedium', label: 'Medium ($2-$10B)' },
      { field: 'mkCapSmall', label: 'Small ($250M-$2B)' },
      { field: 'mkCapMicro', label: 'Micro (< $250M)' },
    ];
    mkCapBuckets.forEach(bucket => {
      if (this.formData[bucket.field]) {
        chips.push({
          color: '#929292',
          option: { name: bucket.label, field: bucket.field },
          disabled: false,
          type: 'marketCapBucket',
          displayName: 'name',
        });
      }
    });

    // Market cap range chip — the low/high inputs are a single constraint, one chip
    if (this.formData.mkCapLow || this.formData.mkCapHigh) {
      const low = this.formData.mkCapLow?.trim();
      const high = this.formData.mkCapHigh?.trim();
      let rangeLabel = '';
      if (low && high) {
        rangeLabel = `$${low} \u2013 $${high}`;
      } else if (low) {
        rangeLabel = `\u2265 $${low}`;
      } else if (high) {
        rangeLabel = `\u2264 $${high}`;
      }
      if (rangeLabel) {
        chips.push({
          color: '#929292',
          option: { name: rangeLabel },
          disabled: false,
          type: 'marketCapRange',
          displayName: 'name',
        });
      }
    }

    this.activeFilterChips = chips;
  }

  /**
   * Locks page scroll while the drawer is open on a phone, so nothing scrolls behind it. CDK's
   * block strategy fixes the body with its offset held in `top` and restores the scroll position
   * on release — `overflow: hidden` alone is ignored by iOS Safari once its toolbar collapses.
   */
  private syncScrollLock(): void {
    if (this.isOpen && this.isMobile) {
      this.blockScrollStrategy.enable();
    } else {
      this.releaseScrollLock();
    }
  }

  /**
   * Tracks the drawer breakpoint; widening past it with the drawer open closes the drawer. Only a change from
   * phone to desktop closes it, not the first reading, so a pane that starts open on desktop stays open.
   */
  private watchBreakpoint(): void {
    this.subscriptions.add(
      this.breakpointObserver.observe(DRAWER_MEDIA_QUERY).subscribe(state => {
        const wasMobile = this.isMobile;
        this.isMobile = state.matches;
        if (wasMobile && !this.isMobile && this.isOpen) {
          this.closePane();
        }
        this.syncScrollLock();
      })
    );
  }

  /** Collapses the FILTERS button on the first page scroll, then stops listening. */
  private watchFirstScroll(): void {
    this.ngZone.runOutsideAngular(() => {
      const onScroll = () => {
        this.removeScrollListener?.();
        this.removeScrollListener = null;
        this.ngZone.run(() => this.fabCollapsed = true);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      this.removeScrollListener = () => window.removeEventListener('scroll', onScroll);
    });
  }

  /** Emits filter changes to the parent and queues a persistence write. */
  private emitAndPersist(): void {
    this.updateFilterEvent.emit(this.formData);
    this.updateActiveFilterChips();
    this.persistSubject.next();
  }

  /** Loads persisted filter state from localStorage on init. */
  private hydrateFromStorage(): void {
    if (!this.storageKey) {
      this.updateFilterEvent.emit(this.formData);
      this.updateActiveFilterChips();
      return;
    }

    const saved = this.persistenceService.loadFilters(this.storageKey);
    if (saved) {
      this.formData = saved;
      this.filterBtnActive = saved.following;
    }

    this.updateFilterEvent.emit(this.formData);
    this.updateActiveFilterChips();
  }

  private loadPortfolios(): void {
    this.portfolioService.getPortfolios().subscribe((resp: PaginatedList<Portfolio>) => {
      this.portfolios = resp.records;
      // Rebuild chips now that we have portfolio names — chips built during hydration
      // before this async call resolved would otherwise fall back to "Portfolio {id}".
      this.updateActiveFilterChips();
    }, () => {
      this.portfolios = [];
    });
  }

  /** Loads domains for building chip labels. */
  private loadDomains(): void {
    this.domainService.domains.subscribe((resp: PaginatedList<Domain>) => {
      if (resp) {
        this.domains = resp.records;
        // Rebuild chips now that we have domain names
        this.updateActiveFilterChips();
      }
    });
  }
}
